import React, { useState } from 'react';
import { useBudget } from './BudgetContext';
import { TransactionItem } from './TransactionItem';
import { TransactionEdit } from './TransactionEdit';
import { TransactionCreation } from './TransactionCreation';
import styles from './TransactionList.module.css';

export const TransactionList: React.FC = () => {
  const {
    filteredTransactions,
    categories,
    activeAccounts,
    isSelectMode,
    selectedIds,
    toggleSelection,
    getCategoryById,
    getAccountById,
    updateTransaction,
    deleteTransaction,
    createTransaction,
    isCreating,
    setIsCreating,
    isReadOnly,
  } = useBudget();

  const [editingId, setEditingId] = useState<string | null>(null);

  const handleEditStart = (id: string) => {
    // Don't allow editing in read-only mode
    if (isReadOnly) return;
    setEditingId(id);
  };

  const handleEditSave = async (id: string, updates: {
    date: string;
    description: string;
    amount: number;
    categoryId: string | null;
  }) => {
    await updateTransaction(id, updates);
    setEditingId(null);
  };

  const handleEditCancel = () => {
    setEditingId(null);
  };

  const handleDelete = async (id: string) => {
    await deleteTransaction(id);
    setEditingId(null);
  };

  const handleCreate = async (dto: {
    date: string;
    description: string;
    amount: number;
    categoryId?: string | null;
    accountId: string;
  }) => {
    await createTransaction(dto);
    setIsCreating(false);
  };

  const handleCreateCancel = () => {
    setIsCreating(false);
  };

  if (filteredTransactions.length === 0 && !isCreating) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyMessage}>No transactions found</p>
        <p className={styles.emptyHint}>
          {isReadOnly
            ? 'This shared budget has no transactions yet'
            : 'Import transactions from a CSV file or add one manually'}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.transactionList}>
      {isCreating && (
        <TransactionCreation
          categories={categories}
          accounts={activeAccounts}
          onSave={handleCreate}
          onCancel={handleCreateCancel}
        />
      )}
      {filteredTransactions.map((transaction) => {
        const category = getCategoryById(transaction.categoryId);
        const account = getAccountById(transaction.accountId);
        const isEditing = editingId === transaction.id;

        if (isEditing) {
          return (
            <TransactionEdit
              key={transaction.id}
              transaction={transaction}
              categories={categories}
              onSave={(updates) => handleEditSave(transaction.id, updates)}
              onCancel={handleEditCancel}
              onDelete={() => handleDelete(transaction.id)}
            />
          );
        }

        return (
          <TransactionItem
            key={transaction.id}
            transaction={transaction}
            category={category}
            account={account}
            onEdit={() => handleEditStart(transaction.id)}
            isSelectMode={isSelectMode}
            isSelected={selectedIds.has(transaction.id)}
            onSelectionToggle={() => toggleSelection(transaction.id)}
          />
        );
      })}
    </div>
  );
};

export default TransactionList;
